import { useState } from 'react';
import axios from 'axios';
import { Badge, Button, Spinner } from '@klaro7/ui';
import { RegimeFiscale, StatoProfilo, type FiscalDocumentType } from '@klaro7/shared-types';

import { useAdminFiscalProfile } from '../hooks/useAdminFiscalProfile';
import { downloadAdminFiscalDocument } from '../services/fiscal-admin.service';
import { extractErrorMessage } from '@/lib/api';
import { downloadBlob } from '@/lib/download';

const euroFmt = new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' });
function formatEuro(amount: number): string {
  return euroFmt.format(amount);
}

function formatDate(value: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' });
}

const REGIME_LABELS: Record<RegimeFiscale, string> = {
  [RegimeFiscale.OCCASIONALE]: 'Occasionale',
  [RegimeFiscale.FORFETTARIO]: 'Forfettario',
  [RegimeFiscale.ORDINARIO]: 'Ordinario',
  [RegimeFiscale.CON_CASSA]: 'Con cassa',
  [RegimeFiscale.ESTERO]: 'Estero',
};

// Badge non ha varianti navy/viola/grigio dedicate: forziamo i colori brand
// via className (twMerge nel componente Badge lascia vincere l'ultima classe).
const REGIME_BADGE_CLASS: Record<RegimeFiscale, string> = {
  [RegimeFiscale.OCCASIONALE]: 'bg-brand-orange text-white',
  [RegimeFiscale.FORFETTARIO]: 'bg-brand-teal text-white',
  [RegimeFiscale.ORDINARIO]: 'bg-brand-navy text-white',
  [RegimeFiscale.CON_CASSA]: 'bg-purple-600 text-white',
  [RegimeFiscale.ESTERO]: 'bg-neutral-400 text-white',
};

const STATO_BADGE: Record<StatoProfilo, { label: string; variant: 'success' | 'error' | 'default' }> = {
  [StatoProfilo.ATTIVO]: { label: 'Attivo', variant: 'success' },
  [StatoProfilo.SOSPESO]: { label: 'Sospeso', variant: 'error' },
  [StatoProfilo.CONVERTITO]: { label: 'Convertito', variant: 'default' },
};

const PARTITA_IVA_REGIMES: RegimeFiscale[] = [
  RegimeFiscale.FORFETTARIO,
  RegimeFiscale.ORDINARIO,
  RegimeFiscale.CON_CASSA,
  RegimeFiscale.ESTERO,
];

const DOCUMENT_LABELS: Record<FiscalDocumentType, string> = {
  ci_fronte: 'Carta d’identità fronte',
  ci_retro: 'Carta d’identità retro',
  cf_fronte: 'Codice fiscale fronte',
  cf_retro: 'Codice fiscale retro',
  visura: 'Visura camerale',
};

function Field({ label, value }: { label: string; value?: string | number | null }) {
  return (
    <div>
      <dt className="text-xs text-neutral-400">{label}</dt>
      <dd className="mt-0.5 text-sm text-neutral-900">
        {value === null || value === undefined || value === '' ? (
          <span className="text-neutral-300">—</span>
        ) : (
          value
        )}
      </dd>
    </div>
  );
}

/** Group an IBAN in blocks of 4 for display only. */
function formatIbanDisplay(iban: string): string {
  return iban.replace(/(.{4})/g, '$1 ').trim();
}

/** Country prefix + bullets + last 4 chars, e.g. "IT•••••••••••••••1234". */
function maskIban(iban: string): string {
  if (iban.length <= 6) return iban;
  return `${iban.slice(0, 2)}${'•'.repeat(iban.length - 6)}${iban.slice(-4)}`;
}

/**
 * IBAN di pagamento del professionista: mascherato di default, con reveal
 * esplicito e copia. Vive sul Professional (non sul profilo fiscale), quindi
 * è mostrato anche quando il profilo fiscale non è ancora compilato.
 */
function IbanSection({ iban, ibanSetAt }: { iban?: string; ibanSetAt?: string }) {
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!iban) return;
    try {
      await navigator.clipboard.writeText(iban);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard non disponibile (http, permessi): nessun feedback
    }
  };

  return (
    <div className="mb-4 border-b border-neutral-100 pb-4">
      <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-neutral-400">
        IBAN per i pagamenti
      </h4>
      {iban ? (
        <div className="flex flex-wrap items-center gap-3">
          <code className="rounded-lg bg-neutral-50 px-3 py-1.5 font-mono text-sm tracking-wide text-neutral-900">
            {revealed ? formatIbanDisplay(iban) : maskIban(iban)}
          </code>
          <Button size="sm" variant="secondary" onClick={() => setRevealed((v) => !v)}>
            {revealed ? 'Nascondi' : 'Mostra'}
          </Button>
          <Button size="sm" variant="secondary" onClick={handleCopy}>
            {copied ? 'Copiato' : 'Copia'}
          </Button>
          {ibanSetAt && (
            <span className="text-xs text-neutral-400">Confermato il {formatDate(ibanSetAt)}</span>
          )}
        </div>
      ) : (
        <p className="text-sm text-neutral-400">
          Non ancora inserito — il professionista lo imposta dalla pagina Compensi.
        </p>
      )}
    </div>
  );
}

function thresholdColor(percentuale: number): string {
  if (percentuale >= 100) return 'bg-red-500';
  if (percentuale >= 80) return 'bg-brand-orange';
  return 'bg-brand-teal';
}

function DocumentRow({
  docType,
  professionalId,
  doc,
}: {
  docType: FiscalDocumentType;
  professionalId: string;
  doc: { originalName: string; uploadedAt: string } | null;
}) {
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDownload = async () => {
    setDownloading(true);
    setError(null);
    try {
      const blob = await downloadAdminFiscalDocument(professionalId, docType);
      downloadBlob(blob, doc?.originalName || docType);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="flex items-center justify-between gap-3 py-2">
      <div>
        <p className="text-sm text-neutral-700">{DOCUMENT_LABELS[docType]}</p>
        {doc ? (
          <p className="text-xs text-neutral-400">
            {doc.originalName} · caricato il {formatDate(doc.uploadedAt)}
          </p>
        ) : (
          <p className="text-xs text-neutral-400">Mancante</p>
        )}
        {error && <p className="text-xs text-red-600">{error}</p>}
      </div>
      {doc && (
        <Button size="sm" variant="secondary" onClick={handleDownload} loading={downloading}>
          Scarica
        </Button>
      )}
    </div>
  );
}

/**
 * Card "Profilo fiscale" nella scheda admin del professionista — solo
 * super-admin. Regime, dati per-regime, soglie della prestazione occasionale
 * con avanzamento, documenti caricati con download.
 */
export function AdminFiscalProfileCard({
  professionalId,
  iban,
  ibanSetAt,
}: {
  professionalId: string;
  iban?: string;
  ibanSetAt?: string;
}) {
  const { data, isLoading, isError, error } = useAdminFiscalProfile(professionalId);

  const notFound = axios.isAxiosError(error) && error.response?.status === 404;
  const profile = data?.profile;

  /** Header + IBAN sono sempre presenti; solo il corpo fiscale è condizionale. */
  const shell = (body: React.ReactNode) => (
    <div className="rounded-xl border border-neutral-200 bg-white p-5">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-neutral-400">
          Profilo fiscale
        </h3>
        {profile && (
          <div className="flex flex-wrap items-center gap-2">
            <Badge className={REGIME_BADGE_CLASS[profile.regimeFiscale]}>
              {REGIME_LABELS[profile.regimeFiscale]}
            </Badge>
            <Badge variant={STATO_BADGE[profile.statoProfilo].variant}>
              {STATO_BADGE[profile.statoProfilo].label}
            </Badge>
            {!profile.isComplete && <Badge variant="warning">Incompleto</Badge>}
          </div>
        )}
      </div>

      <IbanSection iban={iban} ibanSetAt={ibanSetAt} />

      {body}
    </div>
  );

  if (isLoading) {
    return shell(
      <div className="flex justify-center py-6">
        <Spinner />
      </div>
    );
  }

  if (isError && !notFound) {
    return shell(<p className="text-sm text-red-600">{extractErrorMessage(error)}</p>);
  }

  if (notFound || !data || !profile) {
    return shell(<p className="text-sm text-neutral-400">Profilo fiscale non ancora compilato.</p>);
  }

  const { ritenuta } = data;
  const isPartitaIva = PARTITA_IVA_REGIMES.includes(profile.regimeFiscale);

  return shell(
    <>
      {/* Dati anagrafici — comuni a tutti i regimi */}
      <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Field label="Codice fiscale" value={profile.codiceFiscale} />
        <Field label="Data di nascita" value={formatDate(profile.dataNascita)} />
        <Field label="Comune di nascita" value={profile.comuneNascita} />
        <Field label="Residenza" value={profile.indirizzoResidenza} />
        <Field label="Anno di riferimento" value={profile.annoRiferimento} />
      </dl>

      {/* Dati P.IVA — forfettario / ordinario / con_cassa / estero */}
      {isPartitaIva && (
        <dl className="mt-4 grid grid-cols-1 gap-4 border-t border-neutral-100 pt-4 sm:grid-cols-2 lg:grid-cols-3">
          <Field label="Partita IVA" value={profile.partitaIVA} />
          <Field label="Ragione sociale" value={profile.ragioneSociale} />
          <Field label="Sede fiscale" value={profile.sedeFiscale} />
          <Field label="Numero REA" value={profile.numeroREA} />
          <Field label="IVA" value={profile.ivaPercentuale != null ? `${profile.ivaPercentuale}%` : null} />
        </dl>
      )}

      {/* Cassa professionale — solo con_cassa */}
      {profile.regimeFiscale === RegimeFiscale.CON_CASSA && profile.cassaProfessionale && (
        <dl className="mt-4 grid grid-cols-1 gap-4 border-t border-neutral-100 pt-4 sm:grid-cols-2">
          <Field label="Cassa professionale" value={profile.cassaProfessionale.nome} />
          <Field label="Percentuale cassa" value={`${profile.cassaProfessionale.percentuale}%`} />
        </dl>
      )}

      {/* Estero — paese + nota ritenuta 0 */}
      {profile.regimeFiscale === RegimeFiscale.ESTERO && (
        <div className="mt-4 border-t border-neutral-100 pt-4">
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Paese di residenza fiscale" value={profile.paeseEstero} />
          </dl>
          <p className="mt-2 text-xs text-neutral-500">Ritenuta 0 — fattura proforma</p>
        </div>
      )}

      {/* Soglie prestazione occasionale */}
      {ritenuta && (
        <div className="mt-4 border-t border-neutral-100 pt-4">
          <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-neutral-400">
            Prestazione occasionale — soglia annua
          </h4>
          <div className="mb-1 h-2 w-full overflow-hidden rounded-full bg-neutral-100">
            <div
              className={`h-full rounded-full transition-all ${thresholdColor(ritenuta.percentuale)}`}
              style={{ width: `${Math.min(100, ritenuta.percentuale)}%` }}
            />
          </div>
          <p className="text-sm text-neutral-700">
            {formatEuro(ritenuta.lordoYtd)} su {formatEuro(ritenuta.sogliaMax)} — residuo{' '}
            {formatEuro(ritenuta.residuo)}
          </p>
          <div className="mt-3 flex gap-4">
            {(
              [
                { key: 'n3000', label: '€ 3.000' },
                { key: 'n4000', label: '€ 4.000' },
                { key: 'n4500', label: '€ 4.500' },
              ] as const
            ).map((t) => (
              <div key={t.key} className="flex items-center gap-1.5 text-xs text-neutral-500">
                <span
                  className={`inline-block h-2.5 w-2.5 rounded-full ${
                    ritenuta.soglieNotifica[t.key] ? 'bg-brand-orange' : 'bg-neutral-200'
                  }`}
                />
                {t.label}
              </div>
            ))}
          </div>
          <dl className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Ritenuta d'acconto" value={`${ritenuta.aliquota}%`} />
            <Field
              label="Bollo"
              value={`${formatEuro(ritenuta.bolloImporto)} oltre ${formatEuro(ritenuta.bolloSoglia)}`}
            />
          </dl>
        </div>
      )}

      {/* Documenti */}
      <div className="mt-4 border-t border-neutral-100 pt-4">
        <h4 className="mb-1 text-xs font-semibold uppercase tracking-wider text-neutral-400">
          Documenti
        </h4>
        <div className="divide-y divide-neutral-100">
          <DocumentRow docType="ci_fronte" professionalId={professionalId} doc={profile.documenti.ci_fronte} />
          <DocumentRow docType="ci_retro" professionalId={professionalId} doc={profile.documenti.ci_retro} />
          <DocumentRow docType="cf_fronte" professionalId={professionalId} doc={profile.documenti.cf_fronte} />
          <DocumentRow docType="cf_retro" professionalId={professionalId} doc={profile.documenti.cf_retro} />
          {isPartitaIva && (
            <DocumentRow docType="visura" professionalId={professionalId} doc={profile.documenti.visura} />
          )}
        </div>
      </div>
    </>
  );
}
